import React, { useRef, useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { CalendarEvent } from './types';

export interface EventChipProps {
  event: CalendarEvent;
  onClick?: (event: CalendarEvent) => void;
  className?: string;
}

const variantMap: Record<NonNullable<CalendarEvent['colorVariant']>, string> = {
  primary: 'bg-primary-50 text-primary-900 border-primary-200',
  secondary: 'bg-secondary-50 text-secondary-900 border-secondary-200',
  accent: 'bg-accent-50 text-accent-900 border-accent-200',
  success: 'bg-success-50 text-success-900 border-success-200',
  warning: 'bg-warning-50 text-warning-900 border-warning-200',
  error: 'bg-error-50 text-error-900 border-error-200',
  gray: 'bg-muted text-foreground border-border',
};

export const EventChip: React.FC<EventChipProps> = ({ event, onClick, className }) => {
  const variant = event.colorVariant ?? 'primary';
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  }, [event.title]);

  return (
    <button
      title={isTruncated ? event.title : undefined}
      type="button"
      className={cn(
        'w-full text-left px-2 py-1 rounded-md border text-xs hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        variantMap[variant],
        className
      )}
      onClick={() => onClick?.(event)}
      aria-label={`Open event ${event.title}`}
    >
      <div className="flex items-center gap-2">
        <span ref={textRef} className="truncate">{event.title}</span>
        {event.status && (
          <span
            className={cn(
              'ml-auto inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium',
              event.status === 'scheduled' && 'bg-primary-100 text-primary-900',
              event.status === 'draft' && 'bg-warning-100 text-warning-900',
              event.status === 'sent' && 'bg-success-100 text-success-900'
            )}
          >
            {event.status}
          </span>
        )}
      </div>
    </button>
  );
};


